/**
 * Ab wieviel Zeichen der Kunde in der Shopping-Kachel nichts mehr sieht.
 *
 * **Anzeigekonvention, keine API-Grenze.** Google kuerzt je nach Platzierung
 * unterschiedlich; 70 ist der konservative Wert fuer die Kachel. Die harte
 * Grenze bleibt `TITLE_MAX_LENGTH = 150` in
 * `supabase/functions/_shared/title-analysis.ts` - ab dort schneiden WIR den
 * Titel ab (`uploadTitleForPayload`, `truncateTitle` am Segment), Google
 * lehnt ihn nicht ab. Wer der 70 widerspricht, aendert genau diese eine
 * Konstante.
 */
export const TILE_TITLE_CUT = 70;

export interface TitleCut {
  /** Was der Kunde liest. */
  visible: string;
  /** Was hinter dem Strich steht. Leer, wenn nichts abgeschnitten wird. */
  hidden: string;
  /** Wird ueberhaupt geschnitten? Bei genau `TILE_TITLE_CUT` Zeichen nicht. */
  isCut: boolean;
}

export function splitAtCut(title: string, cut: number = TILE_TITLE_CUT): TitleCut {
  // Ueber Codepunkte statt ueber `slice`: `slice` trennt zusammengesetzte
  // Zeichen (Emoji, einige CJK-Zeichen) mitten in der UTF-16-Kodierung und
  // erzeugt ein halbes Zeichen im Ergebnis.
  const points = Array.from(title);
  if (points.length <= cut) return { visible: title, hidden: '', isCut: false };
  return {
    visible: points.slice(0, cut).join(''),
    hidden: points.slice(cut).join(''),
    isCut: true,
  };
}

/**
 * Die harte Grenze: ab hier wird der Titel beim Hochladen abgeschnitten.
 *
 * **Spiegel, keine Quelle.** Durchgesetzt wird sie serverseitig an zwei
 * Stellen, und zwar beide in UTF-16-Einheiten:
 *   - `supabase/functions/_shared/product-validation.ts:233`
 *     (`optimized_title.length > TITLE_MAX_LENGTH` -> Warnung `title_too_long`)
 *   - `supabase/functions/_shared/uploaded-title.ts`, Helfer
 *     `uploadTitleForPayload` (`truncateTitle(..., 150).trim()`, Segment- dann Wortgrenze; der Aufrufer ist
 *     `process-upload-queue`). Keine Zeilennummer: die wandert, der
 *     Helfername nicht.
 *
 * Nicht Google kuerzt, WIR kuerzen, seit dem Titel-Waechter am Segment statt
 * mitten im Wort; die Anzeige im Editor zaehlt weiter Zeichen. Die Copy sagt
 * das auch so (`fields.titleCharsOver`).
 *
 * Deshalb rechnet `titleLengthState` in UTF-16-Einheiten (`.length`) und NICHT
 * in Codepunkten wie `splitAtCut`.
 *
 * **Was dabei genau gilt, und was nicht.** Als Ungleichung halten die beiden
 * Einheiten zusammen: die Codepunkt-Zahl 70 wird nur genannt, wenn
 * `splitAtCut(...).isCut` wahr ist, und dann ist sie zwangslaeufig kleiner als
 * die UTF-16-Laenge. Als SATZ halten sie es nicht: bei einem Titel aus 60
 * Buchstaben und 45 Emoji sind es 105 Codepunkte, aber 150 UTF-16-Einheiten -
 * der Zaehler nennt dann 150 und 70, waehrend der Nutzer 105 Zeichen zaehlt.
 * Der Grund, es trotzdem so zu rechnen: 150 ist die Zahl, an der WIRKLICH
 * geschnitten wird, und ein Zaehler, der in Codepunkten "148 von 150" meldet,
 * waehrend der Upload bereits kuerzt, waere der teurere Fehler. Reale
 * Produkttitel mit Emoji sind der Randfall, ein Schnitt mitten im Wort nicht.
 */
export const TITLE_MAX_LENGTH = 150;

/**
 * Ab hier faerbt der Zaehler, obwohl noch nichts kaputt ist.
 *
 * 71,0 Prozent der optimierten Titel im Bestand (179.965 von 253.325) liegen
 * zwischen 130 und 150 Zeichen. Wer im Editor ein Wort ergaenzt, reisst die
 * Grenze mit hoher Wahrscheinlichkeit. Ein Zaehler, der erst hinterher meldet,
 * kommt fuer den haeufigsten Fehler zu spaet.
 */
export const TITLE_LENGTH_WARN_AT = 140;

export type TitleLengthState = 'ok' | 'near' | 'over';

export function titleLengthState(title: string): TitleLengthState {
  const laenge = title.length;
  if (laenge > TITLE_MAX_LENGTH) return 'over';
  if (laenge >= TITLE_LENGTH_WARN_AT) return 'near';
  return 'ok';
}
